import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import styled from 'styled-components';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';

import ReactTableWrapper from 'components/table/ReactTableWrapper';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import { ISelectOptionProps } from 'components/form/types';
import Loader from 'components/Loader';
import { IDropdowns } from './types';

interface IRoleData {
  id: number;
  name: string;
}

interface IGroupData {
  id: string;
  name: string;
  roles: IRoleData[];
  availableRoles: IRoleData[];
}

interface IFormattedData {
  groups: Record<string, { name: string; roles: IRoleData[] }>;
}

interface IProps {
  methods: any;
  btnLabel: string;
  title?: string;
  roleOptions: ISelectOptionProps[];
  userDataOptions: IDropdowns;
  isEdit?: boolean;
  client?: any;
  setIsManageGroupRoleLimits?: (value: boolean) => void;
}

const EmployeeGroupRoleLimits: FC<IProps> = ({
  methods,
  btnLabel,
  title,
  roleOptions,
  userDataOptions,
  isEdit,
  client,
  setIsManageGroupRoleLimits,
}: IProps) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { id: userId } = useParams<{ id: string | undefined }>();
  const { watch } = methods;
  const { showModal } = useModal();

  const [allRoles, setAllRoles] = useState<IRoleData[]>([]);
  const [groupRoleLimits, setGroupRoleLimits] = useState<IFormattedData>();
  const [loading, setLoading] = useState<boolean>(false);

  // watch to enable re-render when employee type is changed
  const employeeType = watch('type') as ISelectOptionProps;
  const isEN = language === 'en';

  const handleSubmit = async () => {
    const formattedData: { groups: Record<string, { roles: number[] }> } = { groups: {} };

    const groups = groupRoleLimits?.groups;
    if (groups && typeof groups === 'object') {
      Object.entries(groups).forEach(([id, group]) => {
        formattedData.groups[id] = {
          roles: group.roles.map(role => role.id),
        };
      });
    }

    try {
      await API.post(`/group-role-limits/${userId}`, formattedData);
      showModal({
        type: 'success',
        content: t('structure.employees.editSuccessMsg'),
      });
      setIsManageGroupRoleLimits?.(false);
    } catch (error) {
      showModal({
        type: 'error',
        content: createErrorFromResponse(error).message,
      });
      setIsManageGroupRoleLimits?.(false);
    }
  };

  useEffect(() => {
    if (userId) {
      setLoading(true);
      const fetchGroupRoleLimits = async () => {
        try {
          const [groupRoleLimitsResponse, userResponse] = await Promise.all([
            API.get(`/group-role-limits/${userId}`),
            API.get(`/users?id=${userId}`),
          ]);

          setGroupRoleLimits(groupRoleLimitsResponse.data);

          // Extract roles from the user API response
          if (userResponse.data?.items?.[0]?.roles) {
            setAllRoles(userResponse.data.items[0].roles);
          }
          setLoading(false);
        } catch (error) {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
          setLoading(false);
        }
      };

      fetchGroupRoleLimits();
    }
  }, [userId]);

  const toggleRole = (groupId: string, roleId: number) => {
    setGroupRoleLimits(prevState => {
      // Deep clone the previous state to ensure immutability
      const updatedState = JSON.parse(JSON.stringify(prevState));

      const currentGroup = updatedState.groups[groupId];
      if (!currentGroup) {
        return prevState;
      }

      const roleExists = currentGroup.roles.some((role: IRoleData) => role.id === roleId);

      if (roleExists) {
        // Remove the role
        currentGroup.roles = currentGroup.roles.filter((role: IRoleData) => role.id !== roleId);
      } else {
        // Add the role
        const roleToAdd = allRoles.find(role => role.id === roleId);
        if (roleToAdd) {
          currentGroup.roles.push(roleToAdd);
        }
      }

      return updatedState;
    });
  };

  const generateColumns = (): Column<Record<string, unknown>>[] => {
    const baseColumns: Column<Record<string, unknown>>[] = [
      {
        Header: t('structure.groups.title') || 'Group Name',
        accessor: 'name',
        Cell: ({ value }: { value: any }) => {
          const cellStyle = { fontWeight: 600 };
          return <div style={cellStyle}>{value}</div>;
        },
      },
    ];

    const roleColumns = allRoles.map(role => ({
      Header: role.name || 'Role',
      accessor: `role_${role.id}`,
      Cell: ({ row, value }: { row: Row<Record<string, unknown>>; value: any }) => {
        const cellStyle = value ? { color: '#28a745', cursor: 'pointer' } : { color: '#dc3545', cursor: 'pointer' };

        return (
          <div
            onClick={() => toggleRole(row.original.id as string, role.id)}
            style={cellStyle}
          >
            {value ? t('common.yes') : t('common.no')}
          </div>
        );
      },
    }));

    return [...baseColumns, ...roleColumns];
  };

  const prepareTableData = () => {
    return Object.entries(groupRoleLimits?.groups || {}).map(([groupId, group]) => {
      const roleFlags: Record<string, boolean> = {};

      allRoles.forEach(role => {
        roleFlags[`role_${role.id}`] = group.roles.some(r => r.id === role.id);
      });

      const groupData: IGroupData = {
        id: groupId,
        name: group.name,
        roles: group.roles,
        availableRoles: allRoles,
      };

      return { ...groupData, ...roleFlags };
    });
  };

  if (
    !isEdit ||
    typeof employeeType?.value !== 'string' ||
    (!employeeType?.value.includes('moderator') && !employeeType?.value.includes('employer'))
  ) {
    return null;
  }

  const tableKey = JSON.stringify(groupRoleLimits);

  return (
    <>
      <BackLink onClick={() => setIsManageGroupRoleLimits?.(false)}>{t('structure.employees.backToEdit')}</BackLink>
      <Form
        title={title}
        methods={methods}
        onSubmit={handleSubmit}
        btnLabel={btnLabel}
        showGlobalError
        isHorizontal
      >
        <FormGroup
          controlId="formGroupFirstName"
          label={t(`${isEN ? 'structure.employees.name_en' : 'structure.employees.name'}`)}
          type="text"
          name="first_name"
          isHorizontal
          isDisabled
        />
        <FormGroup
          controlId="formGroupMiddleName"
          label={t(`${isEN ? 'structure.employees.middleName_en' : 'structure.employees.middleName'}`)}
          type="text"
          name="middle_name"
          isHorizontal
          isDisabled
        />
        <FormGroup
          controlId="formGroupLastName"
          label={t(`${isEN ? 'structure.employees.lastName_en' : 'structure.employees.lastName'}`)}
          type="text"
          name="last_name"
          isHorizontal
          isDisabled
        />

        {loading ? (
          <Loader />
        ) : (
          <ReactTableWrapper
            key={tableKey}
            columns={generateColumns()}
            data={prepareTableData()}
            showPagination={false}
            hideSearch
          />
        )}
      </Form>
    </>
  );
};

const BackLink = styled.a`
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;
  display: block;
  margin-bottom: 1rem;

  &:hover {
    color: ${props => props.theme.colors.primaryHover};
  }
`;

export default EmployeeGroupRoleLimits;
